import { initializeApp, getApps } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { firebaseConfig, db } from '../firebase';

// Secondary app instance — used to create users without logging out the current staff user
const secondaryApp = getApps().find((a) => a.name === 'Secondary')
  ?? initializeApp(firebaseConfig, 'Secondary');

const secondaryAuth = getAuth(secondaryApp);

/**
 * Creates a Firebase Auth user + Firestore user doc, then sends a branded
 * invite email via our Cloud Function (IONOS SMTP, same template as all other emails).
 *
 * Returns { uid, inviteSent } — inviteSent is false if the email Cloud Function
 * failed but the user was still created successfully.
 */
export async function createPortalUser({ email, role, name, clubSchool, createdByEmail, portals, brand, footballOpsPermissions }) {
  // Random temporary password — user will never see this, they set their own via the email link
  const tempPassword = Array.from(crypto.getRandomValues(new Uint8Array(24)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  // Create the auth user via secondary app (doesn't affect current session)
  const credential = await createUserWithEmailAndPassword(
    secondaryAuth,
    email.trim().toLowerCase(),
    tempPassword
  );
  const uid = credential.user.uid;

  // Immediately sign out of secondary app
  await signOut(secondaryAuth);

  // Save user record to Firestore.
  // Setting sendInvite: true causes the onUserCreated Cloud Function trigger
  // to fire and send the branded IONOS invite email — no callable needed.
  await setDoc(doc(db, 'users', uid), {
    email: email.trim().toLowerCase(),
    roles: [role],
    role, // kept for backward compat with legacy reads
    name: name.trim(),
    ...(clubSchool && clubSchool.trim() ? { clubSchool: clubSchool.trim() } : {}),
    ...(Array.isArray(portals) && portals.length ? { portals } : {}),
    // What a Football Operations user can see (admin-set; missing = everything).
    ...(footballOpsPermissions ? { footballOpsPermissions } : {}),
    // Stamps which brand the account was opened under, so the customer →
    // sales-lead mirroring routes them to the right portal's list. Without it
    // every customer defaults to UK → Spain.
    ...(brand ? { brand } : {}),
    createdByEmail,
    createdAt: serverTimestamp(),
    sendInvite: true,
  });

  return { uid, inviteSent: true };
}
